import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  KnowledgeDetail,
  KnowledgeFolder,
  KnowledgeListItem,
  PublicKnowledge,
  PublicKnowledgeListItem,
} from '@erp/shared';

export function knowledgeFoldersOptions() {
  return queryOptions({
    queryKey: ['erp', 'knowledge', 'folders'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<KnowledgeFolder[]>('/knowledge/folders', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface KnowledgeListParams {
  folder_id?: number;
  search?: string;
  sort?: 'az' | 'recent';
}

export function knowledgeListOptions(params: KnowledgeListParams) {
  return queryOptions({
    queryKey: ['erp', 'knowledge', 'list', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<KnowledgeListItem[]>('/knowledge', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function knowledgeDetailOptions(knowledgeId: number) {
  return queryOptions({
    queryKey: ['erp', 'knowledge', 'detail', knowledgeId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<KnowledgeDetail>(`/knowledge/${knowledgeId}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Base pública (/kb) ──────────────────────────────────────────────────────
// Fetch cru SEM bearer e SEM o handler de 401 do api(): as páginas públicas
// rodam fora do AuthGate e um token expirado no storage jamais pode disparar
// o fluxo de login do visitante anônimo.
export async function publicGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/public${path}`, { signal: signal ?? AbortSignal.timeout(30_000) });
  } catch (err) {
    throw { status: 0, error: err instanceof Error ? err.message : 'network_error' };
  }
  if (!res.ok) {
    let body: { error?: string } = {};
    try {
      body = (await res.json()) as { error?: string };
    } catch {
      /* corpo de erro não-JSON */
    }
    throw { status: res.status, error: body.error ?? `http_${res.status}` };
  }
  return (await res.json()) as T;
}

export function publicKbListOptions() {
  return queryOptions({
    queryKey: ['erp', 'knowledge', 'public', 'list'] as const,
    queryFn: ({ signal }) => publicGet<PublicKnowledgeListItem[]>('/kb', signal),
  });
}

export function publicKbArticleOptions(slug: string) {
  return queryOptions({
    queryKey: ['erp', 'knowledge', 'public', 'article', slug] as const,
    queryFn: ({ signal }) => publicGet<PublicKnowledge>(`/kb/${encodeURIComponent(slug)}`, signal),
  });
}

export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    return (err as { error: string }).error;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}
